/**
 * KUSULM PREMIUM UX — kusulm-premium-ux.js
 * Handles:
 *  1. Psychological pricing — Dhs. savings badge
 *  2. Intent-based ATC highlight
 *  3. Variant image switch on swatch/pill click
 *  4. Hover zoom (CSS does it, JS adds class for precision)
 *  5. Preload hero images
 *  6. Quick View (enhanced)
 *  7. Scroll-based header transformation
 *  8. Cart pop animation
 */

(function () {
  'use strict';

  /* ══════════════════════════════════════════════
     1. PSYCHOLOGICAL PRICING — Dhs. savings badge
  ══════════════════════════════════════════════ */
  function initPsychologicalPricing() {
    document.querySelectorAll('.price--on-sale').forEach(function (priceEl) {
      if (priceEl.dataset.kPriceDone) return;
      priceEl.dataset.kPriceDone = '1';

      var saleEl    = priceEl.querySelector('.price-item--sale');
      var compareEl = priceEl.querySelector('.price-item--compare');
      if (!saleEl || !compareEl) return;

      // Parse raw prices from data attributes if available
      var salePrice    = parseFloat(priceEl.closest('[data-product-price]')?.dataset.salePrice    || saleEl.dataset.salePrice    || 0);
      var comparePrice = parseFloat(priceEl.closest('[data-product-price]')?.dataset.comparePrice || compareEl.dataset.comparePrice || 0);

      if (!salePrice || !comparePrice || comparePrice <= salePrice) return;

      var saving = comparePrice - salePrice;
      var formattedSaving = 'Dhs. ' + saving.toFixed(2).replace(/\.00$/, '');

      // Remove existing % badge if any
      var existingBadge = priceEl.querySelector('.badge--sale, .price__badge-sale');
      if (existingBadge) existingBadge.remove();

      // Inject currency savings badge
      var badge = document.createElement('span');
      badge.className = 'k-price-save-badge';
      badge.textContent = 'Save ' + formattedSaving;
      badge.setAttribute('aria-label', 'Save ' + formattedSaving);
      compareEl.after(badge);
    });
  }

  /* ══════════════════════════════════════════════
     2. INTENT-BASED ATC HIGHLIGHT
  ══════════════════════════════════════════════ */
  function initIntentCTA() {
    // Highlight ATC when user hovers product images or scrolls to product form
    document.addEventListener('mouseover', function (e) {
      var card = e.target.closest('.card-wrapper');
      if (card) {
        var atcBtn = card.querySelector('.quick-add__submit, .card__content .button');
        if (atcBtn) atcBtn.classList.add('k-atc-intent');
      }
    });

    document.addEventListener('mouseout', function (e) {
      var card = e.target.closest('.card-wrapper');
      if (card) {
        card.querySelectorAll('.k-atc-intent').forEach(function (btn) {
          btn.classList.remove('k-atc-intent');
        });
      }
    });

    // On product page — highlight ATC when user scrolls near the form
    var atcBtn = document.querySelector('.product-form__submit');
    if (atcBtn) {
      var atcObserver = new IntersectionObserver(function (entries) {
        if (entries[0].isIntersecting) {
          atcBtn.classList.add('k-atc-intent');
          setTimeout(function () { atcBtn.classList.remove('k-atc-intent'); }, 1800);
          atcObserver.disconnect();
        }
      }, { threshold: 0.5 });
      atcObserver.observe(atcBtn);
    }
  }

  /* ══════════════════════════════════════════════
     3. VARIANT IMAGE SWITCH
     Swaps main product image on swatch/pill click
  ══════════════════════════════════════════════ */
  function initVariantImageSwitch() {
    document.addEventListener('change', function (e) {
      var input = e.target;

      // Only for variant radio inputs
      if (!input.matches('input[name^="options"]')) return;

      var form = input.closest('product-info, .product, form');
      if (!form) return;

      var productId = form.dataset.productId || form.querySelector('[data-product-id]')?.dataset.productId;
      var productJSON = document.getElementById('ProductJSON-' + productId) ||
                        document.querySelector('[data-product-id="' + productId + '"] [type="application/json"]');
      if (!productJSON) return;

      try {
        var product = JSON.parse(productJSON.textContent);
        var selectedValues = {};

        form.querySelectorAll('input[name^="options"]:checked, select[name^="options"]').forEach(function (el) {
          var posMatch = el.name.match(/options\[(.+)\]/);
          if (posMatch) selectedValues[posMatch[1]] = el.tagName === 'SELECT' ? el.value : el.value;
        });

        var matchedVariant = product.variants.find(function (v) {
          return Object.values(selectedValues).every(function (val) {
            return v.options.includes(val);
          });
        });

        if (matchedVariant && matchedVariant.featured_image) {
          var img = matchedVariant.featured_image;
          var mainImg = form.closest('.product') &&
                        form.closest('.product').querySelector('.product__media img, .product-media-gallery img');
          if (mainImg) {
            // Smooth crossfade
            mainImg.style.opacity = '0';
            mainImg.style.transition = 'opacity 0.25s ease';
            var newSrc = img.src.replace(/\?.*$/, '') + '?width=800';
            mainImg.src = newSrc;
            mainImg.onload = function () {
              mainImg.style.opacity = '1';
            };
          }
        }
      } catch (err) { /* silent */ }
    });
  }

  /* ══════════════════════════════════════════════
     4. PRELOAD HERO IMAGES
  ══════════════════════════════════════════════ */
  function preloadHeroImages() {
    // Find the first above-fold banner image and preload it
    var heroImg = document.querySelector(
      '.banner__media img, .slideshow__slide:first-child img, .video-section__poster img'
    );
    if (heroImg) {
      var src = heroImg.src || heroImg.dataset.src;
      if (src) {
        var link = document.createElement('link');
        link.rel = 'preload';
        link.as = 'image';
        link.href = src;
        if (heroImg.srcset) link.imageSrcset = heroImg.srcset;
        if (heroImg.sizes)  link.imageSizes  = heroImg.sizes;
        document.head.appendChild(link);
      }
    }
  }

  /* ══════════════════════════════════════════════
     5. SCROLL-BASED HEADER TRANSFORMATION
     Header becomes more compact / changes bg on scroll
  ══════════════════════════════════════════════ */
  function initScrollHeader() {
    var header = document.querySelector('.section-header');
    if (!header) return;

    var ticking = false;
    var THRESHOLD = 80;

    window.addEventListener('scroll', function () {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(function () {
        if (window.scrollY > THRESHOLD) {
          header.classList.add('k-header-scrolled');
        } else {
          header.classList.remove('k-header-scrolled');
        }
        ticking = false;
      });
    }, { passive: true });
  }

  /* ══════════════════════════════════════════════
     6. CART COUNT BOUNCE
  ══════════════════════════════════════════════ */
  function initCartBounce() {
    document.addEventListener('cart:updated', function () {
      var bubble = document.querySelector('.cart-count-bubble');
      if (!bubble) return;
      bubble.classList.remove('animated');
      requestAnimationFrame(function () {
        requestAnimationFrame(function () {
          bubble.classList.add('animated');
          setTimeout(function () { bubble.classList.remove('animated'); }, 500);
        });
      });
    });

    // Also fire on successful ATC
    document.addEventListener('submit', function (e) {
      var form = e.target.closest('[action="/cart/add"]');
      if (!form) return;
      setTimeout(function () {
        var bubble = document.querySelector('.cart-count-bubble');
        if (bubble) {
          bubble.classList.add('animated');
          setTimeout(function () { bubble.classList.remove('animated'); }, 500);
        }
      }, 800);
    });
  }

  /* ══════════════════════════════════════════════
     7. PRICING — update badges on variant change
  ══════════════════════════════════════════════ */
  document.addEventListener('change', function (e) {
    if (e.target.matches('[data-variant-select], input[name^="options"]')) {
      setTimeout(initPsychologicalPricing, 200);
    }
  });

  // Also re-run after variant picker updates DOM
  var variantObserver = new MutationObserver(function (mutations) {
    var needsUpdate = mutations.some(function (m) {
      return m.target.closest && m.target.closest('.price');
    });
    if (needsUpdate) initPsychologicalPricing();
  });
  document.querySelectorAll('.price').forEach(function (el) {
    variantObserver.observe(el, { childList: true, subtree: true, characterData: true });
  });

  /* ══════════════════════════════════════════════
     INIT
  ══════════════════════════════════════════════ */
  function init() {
    initPsychologicalPricing();
    initIntentCTA();
    initVariantImageSwitch();
    preloadHeroImages();
    initScrollHeader();
    initCartBounce();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Re-init after quick view / section loads
  document.addEventListener('shopify:section:load', function () {
    initPsychologicalPricing();
    initIntentCTA();
  });

})();
